import React, { useState } from 'react';
import { optionToken, optClasses } from './optionColor';
import { SheetShell } from './SheetShell';

/**
 * ValueSheet (§4 + erratum E-1). ONE component in TWO shapes — it does NOT read the breakpoint; the CALLER
 * passes `shape`. CardRow always passes 'sheet' (bottom-sheet, follows the card), GridRow always passes
 * 'popover' (anchored to the table cell, follows the table). The popover therefore never appears over a
 * scrolling card list (the 900px iPad case §4 rejected). The scope-bearing `title` is shown in BOTH shapes.
 *
 * The container (backdrop, shape switch, Escape/outside dismiss, backdrop-bubbling guard) lives in
 * SheetShell — extracted so the goal editor and the confirm dialog render through the SAME shell rather
 * than forking it (11-Goal D-G3 was exactly that fork). This file owns only the option-list body.
 */
export interface ValueSheetOption { value: string | null; label: string; }
export interface ValueSheetProps {
  shape: 'sheet' | 'popover';
  title: string;                               // scope-bearing (e.g. "Estado — Nahuel") — kept in both shapes
  options: ValueSheetOption[];
  current: string | null;
  anchor?: HTMLElement | null;                 // popover anchor (ignored for sheet)
  allowCreate?: boolean;
  onSelect: (value: string | null) => void;    // optimistic — caller applies + shows the undo toast
  onClose: () => void;
}

const OptionRow: React.FC<{ opt: ValueSheetOption; selected: boolean; onPick: () => void }> = ({ opt, selected, onPick }) => {
  const { bg, fg } = optClasses(optionToken(opt.value));
  return (
    <button type="button" onClick={onPick}
      className={`flex items-center gap-2 w-full ps-3 pe-3 py-2 text-start rounded-lg hover:bg-canvas ${selected ? 'bg-canvas' : ''}`}>
      <span className={`inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold ${bg} ${fg}`}>{opt.label}</span>
      {selected && <span aria-hidden className="ms-auto text-primary">&#10003;{/* ✓ */}</span>}
    </button>
  );
};

export const ValueSheet: React.FC<ValueSheetProps> = ({ shape, title, options, current, anchor, allowCreate, onSelect, onClose }) => {
  const [draft, setDraft] = useState('');

  return (
    <SheetShell shape={shape} anchor={anchor} onClose={onClose} className={shape === 'popover' ? 'w-64' : ''}>
      <div data-testid="value-sheet-title" className="px-3 pt-3 pb-1 text-[11px] font-semibold text-text-muted uppercase tracking-wide">{title}</div>
      <div className="p-1">
        <OptionRow opt={{ value: null, label: '— (none)' }} selected={current == null} onPick={() => onSelect(null)} />
        {options.map((o) => <OptionRow key={o.value ?? '∅'} opt={o} selected={o.value === current} onPick={() => onSelect(o.value)} />)}
        {allowCreate && (
          <form className="flex items-center gap-2 px-2 py-2" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) onSelect(draft.trim()); }}>
            <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="New value…"
              className="flex-1 min-w-0 h-8 px-2 rounded-lg border border-border-neutral text-sm" dir="auto" />
            <button type="submit" className="h-8 px-3 rounded-lg bg-primary text-white text-sm font-semibold">Add</button>
          </form>
        )}
      </div>
    </SheetShell>
  );
};
